<template>
  <div class="zpl-editor-page">
    <div class="page-container">
      <!-- Editor de ZPL -->
      <div class="zpl-editor">
        <h3>✏️ Editor ZPL</h3>

        <textarea v-model="zplData" class="zpl-textarea" placeholder="Ingresa tu código ZPL aquí..." rows="10"></textarea>

        <div class="editor-controls">
          <button @click="clearEditor" class="control-btn">Limpiar</button>
          <button @click="copyToClipboard" class="control-btn">Copiar</button>
        </div>
      </div>

      <!-- Previsualización -->
      <div class="preview-section">
        <h3>👁️ Previsualización</h3>

        <div class="preview-container">
          <ZplPreview
            :data="zplData"
            :density="density"
            :width="labelWidth"
            :height="labelHeight"
            :showControls="true"
            @export="handleExport"
          />
        </div>
      </div>

      <!-- Configuración -->
      <div class="config-section">
        <h3>⚙️ Configuración</h3>

        <div class="config-grid">
          <div class="config-item">
            <label>Densidad (DPI):</label>
            <input v-model.number="density" type="number" min="72" max="600" step="1" class="config-input" />
          </div>

          <div class="config-item">
            <label>Ancho (mm):</label>
            <input v-model.number="labelWidth" type="number" min="20" max="200" step="1" class="config-input" />
          </div>

          <div class="config-item">
            <label>Alto (mm):</label>
            <input v-model.number="labelHeight" type="number" min="20" max="200" step="1" class="config-input" />
          </div>
        </div>
      </div>

      <!-- Resultado de exportación -->
      <div v-if="exportedImage" class="export-section">
        <h3>📤 Imagen Exportada</h3>

        <div class="export-result">
          <img :src="exportedImage" alt="ZPL Preview" class="exported-image" />

          <div class="export-info">
            <p><strong>Formato:</strong> PNG</p>
            <p><strong>Tamaño:</strong> {{ exportedImageSize }}</p>
            <button @click="downloadExportedImage" class="control-btn">Descargar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ZplPreview from '@/components/domain/label/ZplPreview'

export default {
  name: 'ZPLEditor',
  components: {
    ZplPreview
  },
  data() {
    return {
      zplData: '',
      density: 203,
      labelWidth: 57,
      labelHeight: 51,
      exportedImage: null,
      exportedImageSize: '',
    }
  },
  methods: {
    clearEditor() {
      this.zplData = '';
    },
    copyToClipboard() {
      navigator.clipboard.writeText(this.zplData).then(() => {
        this.$snackbar.success({
          message: 'ZPL copiado al portapapeles'
        });
      }).catch(() => {
        this.$snackbar.error({
          message: 'Error al copiar al portapapeles'
        });
      })
    },
    handleExport(base64Image) {
      this.exportedImage = base64Image;
      this.exportedImageSize = this.calculateImageSize(base64Image);

      this.$snackbar.success({
        message: 'Imagen exportada exitosamente'
      });
    },
    calculateImageSize(base64String) {
      const stringLength = base64String.length;
      const sizeInBytes = 4 * Math.ceil(stringLength / 3) * 0.75;
      const sizeInKB = (sizeInBytes / 1024).toFixed(2);
      return `${sizeInKB} KB`;
    },
    downloadExportedImage() {
      if (!this.exportedImage) return;

      const link = document.createElement('a');
      link.download = `zpl-preview-${Date.now()}.png`;
      link.href = this.exportedImage;
      link.click();
    },
  }
}
</script>

<style scoped>
.zpl-editor-page {
  padding: 2rem;
  overflow-y: auto;
  max-width: 1400px;
  margin: 60px auto 0 auto;
  height: calc(100vh - 60px);
}

.page-container {
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr 1fr;
}

.zpl-editor,
.preview-section {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.zpl-editor h3,
.preview-section h3,
.config-section h3,
.export-section h3 {
  margin: 0 0 1rem 0;
  color: #495057;
}

.zpl-textarea {
  width: 100%;
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 200px;
}

.zpl-textarea:focus {
  outline: none;
  border-color: #053186;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.editor-controls {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.control-btn {
  background: #053186;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  font-size: 14px;
  border: 1px solid #053186;
}

.control-btn:hover {
  color: #053186;
  background: transparent;
}

.preview-container {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.config-section {
  grid-column: 1 / -1;
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.config-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.config-item label {
  font-weight: 500;
  color: #495057;
}

.config-input {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
}

.config-input:focus {
  outline: none;
  border-color: #053186;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
}

.export-section {
  grid-column: 1 / -1;
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.export-result {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}

.exported-image {
  max-width: 300px;
  max-height: 200px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.export-info {
  flex: 1;
}

.export-info p {
  margin: 0.5rem 0;
  color: #495057;
}

@media (max-width: 768px) {
  .page-container {
    grid-template-columns: 1fr;
  }

  .export-result {
    flex-direction: column;
  }
}
</style>
